import { animate, stagger, utils } from 'animejs'
import type { AnimationParams, JSAnimation } from 'animejs'

/**
 * The single motion surface for the app (ADR-013). Every animation goes through
 * here so the reduced-motion rule is enforced in one place: when the user asks
 * for reduced motion (or we are on the server) each function applies the end
 * state synchronously and resolves at once. Promises never reject; starting a
 * new animation on an element resolves the previous one on that element.
 */

const REDUCED_QUERY = '(prefers-reduced-motion: reduce)'
const MAX_STAGGER_TOTAL_MS = 320

const EASE_OUT = 'easeOutCubic'
const EASE_IN_OUT = 'easeInOutQuad'

const pending = new WeakMap<Element, () => void>()
const tweens = new WeakMap<object, JSAnimation>()

function enabled(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return !window.matchMedia(REDUCED_QUERY).matches
}

type EndState = Partial<Pick<CSSStyleDeclaration, 'opacity' | 'transform' | 'width'>>

function settle(els: HTMLElement[], end: EndState): Promise<void> {
  els.forEach((el) => Object.assign(el.style, end))
  return Promise.resolve()
}

function run(els: HTMLElement[], params: AnimationParams, end: EndState): Promise<void> {
  if (els.length === 0) return Promise.resolve()
  if (!enabled()) return settle(els, end)

  els.forEach((el) => {
    pending.get(el)?.()
    utils.remove(el)
  })

  return new Promise((resolve) => {
    const done = () => {
      els.forEach((el) => pending.delete(el))
      resolve()
    }
    els.forEach((el) => pending.set(el, done))
    animate(els, { ...params, onComplete: done })
  })
}

function enter(el: HTMLElement, o: { dx?: number; ms?: number } = {}): Promise<void> {
  const { dx = 24, ms = 220 } = o
  return run([el], { opacity: [0, 1], translateX: [dx, 0], duration: ms, ease: EASE_OUT }, {
    opacity: '1',
    transform: '',
  })
}

function leave(el: HTMLElement, o: { dx?: number; ms?: number } = {}): Promise<void> {
  const { dx = -24, ms = 180 } = o
  return run([el], { opacity: [1, 0], translateX: [0, dx], duration: ms, ease: EASE_OUT }, {
    opacity: '0',
  })
}

/**
 * Enters from below and settles — for a surface that belongs to the bottom edge
 * (the flash toast) or that appears in place of a form (an inline notice). The
 * sideways `enter` above would read as a panel sliding in from off-screen.
 */
function rise(el: HTMLElement, o: { dy?: number; ms?: number } = {}): Promise<void> {
  const { dy = 14, ms = 260 } = o
  return run([el], { opacity: [0, 1], translateY: [dy, 0], duration: ms, ease: EASE_OUT }, {
    opacity: '1',
    transform: '',
  })
}

/** The reverse of `rise`: settles back towards the edge it came from. */
function sink(el: HTMLElement, o: { dy?: number; ms?: number } = {}): Promise<void> {
  const { dy = 10, ms = 180 } = o
  return run([el], { opacity: [1, 0], translateY: [0, dy], duration: ms, ease: EASE_OUT }, {
    opacity: '0',
  })
}

function staggerIn(
  targets: Iterable<HTMLElement>,
  o: { step?: number; ms?: number } = {},
): Promise<void> {
  const els = Array.from(targets)
  if (els.length === 0) return Promise.resolve()
  const { step = 40, ms = 360 } = o
  const cappedStep = Math.min(step, Math.floor(MAX_STAGGER_TOTAL_MS / els.length))
  if (enabled()) els.forEach((el) => { el.style.opacity = '0' })
  return run(
    els,
    { opacity: [0, 1], translateY: [10, 0], duration: ms, delay: stagger(cappedStep), ease: EASE_OUT },
    { opacity: '1', transform: '' },
  )
}

function flip(inner: HTMLElement, toBack: boolean, o: { ms?: number } = {}): Promise<void> {
  const { ms = 420 } = o
  const deg = toBack ? 180 : 0
  return run([inner], { rotateY: deg, duration: ms, ease: EASE_IN_OUT }, {
    transform: `rotateY(${deg}deg)`,
  })
}

/**
 * Slides an element sideways and leaves it there — used to move the passage clear of
 * the word panel. A shift of 0 clears the transform outright rather than settling on
 * `translateX(0px)`: a transform, even an identity one, makes the element a containing
 * block for any `position: fixed` descendant.
 */
function shiftX(el: HTMLElement, px: number, o: { ms?: number } = {}): Promise<void> {
  const { ms = 260 } = o
  return run([el], { translateX: px, duration: ms, ease: EASE_OUT }, {
    transform: px === 0 ? '' : `translateX(${px}px)`,
  }).then(() => {
    if (px === 0) el.style.transform = ''
  })
}

function grow(el: HTMLElement, toWidthPct: number, o: { ms?: number } = {}): Promise<void> {
  const { ms = 400 } = o
  return run([el], { width: `${toWidthPct}%`, duration: ms, ease: EASE_OUT }, {
    width: `${toWidthPct}%`,
  })
}

/**
 * Drives a 0→1 progress value for a caller that interpolates its own geometry (an
 * SVG path, a set of coordinates). Starting a new tween under the same `key` pauses
 * the one before it, so a slider dragged quickly never fights itself. Under reduced
 * motion the end state is applied at once.
 */
function tween(
  onUpdate: (t: number) => void,
  o: { ms?: number; key?: object } = {},
): Promise<void> {
  const { ms = 480, key } = o
  if (!enabled()) {
    onUpdate(1)
    return Promise.resolve()
  }
  if (key) tweens.get(key)?.pause()
  return new Promise((resolve) => {
    const progress = { t: 0 }
    const animation = animate(progress, {
      t: 1,
      duration: ms,
      ease: EASE_OUT,
      onUpdate: () => onUpdate(progress.t),
      onComplete: () => {
        onUpdate(1)
        if (key) tweens.delete(key)
        resolve()
      },
    })
    if (key) tweens.set(key, animation)
  })
}

/**
 * Draws SVG strokes in one after another by unwinding their dash offset — the
 * pen-on-paper entrance for a chart. Resolves at once where a path cannot report
 * its length (jsdom) or motion is reduced.
 */
function drawIn(
  paths: Iterable<SVGPathElement>,
  o: { ms?: number; step?: number } = {},
): Promise<void> {
  const els = Array.from(paths).filter((el) => typeof el.getTotalLength === 'function')
  if (els.length === 0 || !enabled()) return Promise.resolve()
  const { ms = 900, step = 120 } = o
  els.forEach((el) => {
    const length = el.getTotalLength()
    el.style.strokeDasharray = `${length}`
    el.style.strokeDashoffset = `${length}`
  })
  return new Promise((resolve) => {
    animate(els, {
      strokeDashoffset: 0,
      duration: ms,
      delay: stagger(step),
      ease: EASE_IN_OUT,
      onComplete: () => {
        els.forEach((el) => {
          el.style.strokeDasharray = ''
          el.style.strokeDashoffset = ''
        })
        resolve()
      },
    })
  })
}

export const motion = { enabled, enter, leave, rise, sink, staggerIn, flip, grow, shiftX, tween, drawIn }
